import { useState, useCallback } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { queryClient } from './queryClient'
import { queryKeys } from './queryKeys'
import { invalidateEntity } from './cacheInvalidation'
import * as optionsApi from './optionsApi'
import * as dashboardApi from './dashboardApi'
import * as allocationApi from './allocationApi'
import api from './client'

/**
 * Universal Query Adapter: maps TanStack Query to standard React hook interface.
 */
function useAppQuery(queryKey, queryFn, options = {}) {
  const result = useQuery({
    queryKey,
    queryFn,
    ...options,
  })
  return {
    ...result,
    isSuccess: result.isSuccess,
    isError: result.isError,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
  }
}

/**
 * Universal Mutation Adapter: returns RTK-compatible tuple [mutateAsync, { isLoading, ... }]
 */
function useAppMutation(mutationFn, options = {}) {
  const mutation = useMutation({
    mutationFn,
    ...options,
  })
  return [
    mutation.mutateAsync,
    {
      ...mutation,
      isLoading: mutation.isPending,
      isSuccess: mutation.isSuccess,
      isError: mutation.isError,
    },
  ]
}

/**
 * Universal Lazy Query Adapter: returns tuple [triggerFn, { data, isLoading, ... }]
 */
function useAppLazyQuery(keyFactory, queryFn) {
  const [params, setParams] = useState(null)
  const [enabled, setEnabled] = useState(false)

  const query = useQuery({
    queryKey: params ? keyFactory(params) : ['lazy_uninit'],
    queryFn: () => queryFn(params),
    enabled: Boolean(enabled && params !== null),
  })

  const trigger = useCallback(
    async (p) => {
      setParams(p)
      setEnabled(true)
      const data = await queryClient.fetchQuery({
        queryKey: keyFactory(p),
        queryFn: () => queryFn(p),
      })
      return { data }
    },
    [keyFactory, queryFn],
  )

  return [trigger, { ...query, isLoading: query.isLoading && enabled }]
}

// ── Dropdown / Option Hooks ──────────────────────────────────────────────────
export function useGetCompanyOptionsQuery(params = {}) {
  return useAppQuery(queryKeys.options.companies(params), () => optionsApi.fetchCompanyOptions(params), {
    staleTime: 1000 * 60 * 5, // 5 minutes fresh
  })
}

export function useGetProjectOptionsQuery(params = {}) {
  return useAppQuery(queryKeys.options.projects(params), () => optionsApi.fetchProjectOptions(params), {
    staleTime: 1000 * 60 * 5,
  })
}

export function useGetUserOptionsQuery(params = {}) {
  return useAppQuery(queryKeys.options.users(params), () => optionsApi.fetchUserOptions(params), {
    staleTime: 1000 * 60 * 5,
  })
}

export function useGetTeamOptionsQuery(params = {}) {
  return useAppQuery(queryKeys.options.teams(params), () => optionsApi.fetchTeamOptions(params), {
    staleTime: 1000 * 60 * 5,
  })
}

export function useGetDepartmentOptionsQuery(params = {}) {
  return useAppQuery(queryKeys.options.departments(params), () => optionsApi.fetchDepartmentOptions(params), {
    staleTime: 1000 * 60 * 5,
  })
}

export function useGetTeamByProjectQuery(projectId) {
  return useAppQuery(['teams', 'by-project', projectId], () => optionsApi.fetchTeamByProject(projectId), {
    enabled: Boolean(projectId),
  })
}

export function useGetRoleOptionsQuery(params = {}) {
  return useAppQuery(queryKeys.options.roles(params), () => optionsApi.fetchRoleOptions(params), {
    staleTime: 1000 * 60 * 5,
  })
}

// ── Dashboard Hooks ──────────────────────────────────────────────────────────
export function useGetDashboardWidgetsQuery() {
  return useAppQuery(['widgets', 'dashboard'], dashboardApi.fetchDashboardWidgets)
}

export function useGetPersonalDashboardQuery(params = {}) {
  return useAppQuery(queryKeys.dashboard.personal(params), () => dashboardApi.fetchPersonalDashboard(params), {
    placeholderData: (prev) => prev,
  })
}

export function useGetFinancialDashboardQuery(params = {}) {
  return useAppQuery(queryKeys.dashboard.financial(params), () => dashboardApi.fetchFinancialDashboard(params), {
    placeholderData: (prev) => prev,
  })
}

export function useLazyGetFinancialDashboardDrilldownQuery() {
  return useAppLazyQuery(
    (params) => queryKeys.dashboard.financialDrilldown(params),
    (params) => dashboardApi.fetchFinancialDashboardDrilldown(params),
  )
}

export function useGetTeamDashboardFiltersQuery(params = {}) {
  return useAppQuery(queryKeys.dashboard.teamFilters(params), () => dashboardApi.fetchTeamDashboardFilters(params))
}

export function useGetTeamDashboardQuery(params = {}) {
  return useAppQuery(queryKeys.dashboard.team(params), () => dashboardApi.fetchTeamDashboard(params), {
    placeholderData: (prev) => prev,
  })
}

export function useGetTeamDashboardAvailabilityQuery(params = {}) {
  return useAppQuery(
    queryKeys.dashboard.teamAvailability(params),
    () => dashboardApi.fetchTeamDashboardAvailability(params),
    { placeholderData: (prev) => prev },
  )
}

export function useGetTeamDashboardScheduleQuery(params = {}) {
  return useAppQuery(
    queryKeys.dashboard.teamSchedule(params),
    () => dashboardApi.fetchTeamDashboardSchedule(params),
    { placeholderData: (prev) => prev },
  )
}

export function useGetTeamDashboardSlaQuery(params = {}) {
  return useAppQuery(
    queryKeys.dashboard.teamSla(params),
    () => dashboardApi.fetchTeamDashboardSla(params),
    { placeholderData: (prev) => prev },
  )
}

export function useLazyGetTeamDashboardDrilldownQuery() {
  return useAppLazyQuery(
    (params) => queryKeys.dashboard.teamDrilldown(params),
    (params) => dashboardApi.fetchTeamDashboardDrilldown(params),
  )
}

export function useGetCalendarViewQuery(params = {}) {
  return useAppQuery(queryKeys.dashboard.calendar(params), () => dashboardApi.fetchCalendarView(params))
}

export function useCreateCalendarMeetingMutation() {
  return useAppMutation(dashboardApi.createCalendarMeeting, {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
  })
}

export function useCreateCalendarWorkTimeMutation() {
  return useAppMutation(dashboardApi.createCalendarWorkTime, {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
  })
}

export function useUpdateCalendarEventMutation() {
  return useAppMutation(dashboardApi.updateCalendarEvent, {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
  })
}

export function useDeleteCalendarEventMutation() {
  return useAppMutation(dashboardApi.deleteCalendarEvent, {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
  })
}

export function useCheckCalendarAvailabilityMutation() {
  return useAppMutation(dashboardApi.checkCalendarAvailability)
}

export function useShareCalendarAvailabilityMutation() {
  return useAppMutation(dashboardApi.shareCalendarAvailability)
}

export function useCreateLeaveMutation() {
  return useAppMutation(dashboardApi.createLeave, {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
  })
}

export function useGetCalendarSharingSettingsQuery() {
  return useAppQuery(queryKeys.dashboard.calendarSettings(), dashboardApi.fetchCalendarSharingSettings)
}

export function useUpdateCalendarSharingSettingsMutation() {
  return useAppMutation(dashboardApi.updateCalendarSharingSettings, {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.calendarSettings() }),
  })
}

// ── Widget Management ────────────────────────────────────────────────────────
export function useGetChartTypesQuery() {
  return useAppQuery(['widgets', 'chart-types'], dashboardApi.fetchChartTypes)
}

export function useGetAggregationTypesQuery() {
  return useAppQuery(['widgets', 'aggregations'], dashboardApi.fetchAggregationTypes)
}

export function useGetDatasetsQuery() {
  return useAppQuery(['widgets', 'datasets'], dashboardApi.fetchDatasets)
}

export function useGetDatasetFieldsQuery(datasetKey) {
  return useAppQuery(['widgets', 'dataset-fields', datasetKey], () => dashboardApi.fetchDatasetFields(datasetKey), {
    enabled: Boolean(datasetKey),
  })
}

export function useCreateWidgetMutation() {
  return useAppMutation(dashboardApi.createWidget, {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
  })
}

export function useAssignWidgetMutation() {
  return useAppMutation(dashboardApi.assignWidget, {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
  })
}

// ── Allocation Board ─────────────────────────────────────────────────────────
export function useGetAllocationBoardQuery(params = {}) {
  return useAppQuery(queryKeys.allocation.board(params), () => allocationApi.fetchAllocationBoard(params))
}

export function useGetUnassignedTasksQuery() {
  return useAppQuery(queryKeys.allocation.unassignedTasks(), allocationApi.fetchUnassignedTasks)
}

export function useAssignTaskMutation() {
  return useAppMutation(allocationApi.assignTask, {
    onSuccess: () => invalidateEntity.allocation(),
  })
}

export function useGetJobPoolQuery() {
  return useAppQuery(queryKeys.allocation.jobPool(), allocationApi.fetchJobPool)
}

export function useAssignJobMutation() {
  return useAppMutation(allocationApi.assignJob, {
    onSuccess: () => invalidateEntity.allocation(),
  })
}

export function useUpdateJobAllocationMutation() {
  return useAppMutation(allocationApi.updateJobAllocation, {
    onSuccess: () => invalidateEntity.allocation(),
  })
}

export function useGetEmployeeAvailabilityForRangeQuery(args) {
  return useAppQuery(
    queryKeys.allocation.availability(args),
    () => allocationApi.fetchEmployeeAvailabilityForRange(args),
    {
      enabled: Boolean(args?.employee_id && args?.start_date && args?.end_date),
    },
  )
}

export function useUpdateTaskAllocationMutation() {
  return useAppMutation(allocationApi.updateTaskAllocation, {
    onSuccess: () => invalidateEntity.allocation(),
  })
}

export function useRecalculateBoardAvailabilityMutation() {
  return useAppMutation(allocationApi.recalculateBoardAvailability, {
    onSuccess: () => invalidateEntity.allocation(),
  })
}

export function useRefreshEmployeeAvailabilityMutation() {
  return useAppMutation(allocationApi.refreshEmployeeAvailability, {
    onSuccess: () => invalidateEntity.allocation(),
  })
}

// ── Google / Microsoft Calendar Placeholders ─────────────────────────────────
export function useGetGoogleCalendarConnectUrlMutation() {
  return useAppMutation(async () => {
    const { data } = await api.get('/calendar/integrations/google/connect')
    return data
  })
}

export function useGetGoogleCalendarStatusQuery() {
  return useAppQuery(['calendar', 'integrations', 'google', 'status'], async () => {
    const { data } = await api.get('/calendar/integrations/google/status')
    return data
  })
}

export function useDisconnectGoogleCalendarMutation() {
  return useAppMutation(async () => {
    const { data } = await api.delete('/calendar/integrations/google/disconnect')
    return data
  })
}

export function useGetMicrosoftCalendarConnectUrlMutation() {
  return useAppMutation(async () => {
    const { data } = await api.get('/calendar/integrations/microsoft/connect')
    return data
  })
}

export function useGetMicrosoftCalendarStatusQuery() {
  return useAppQuery(['calendar', 'integrations', 'microsoft', 'status'], async () => {
    const { data } = await api.get('/calendar/integrations/microsoft/status')
    return data
  })
}

export function useDisconnectMicrosoftCalendarMutation() {
  return useAppMutation(async () => {
    const { data } = await api.delete('/calendar/integrations/microsoft/disconnect')
    return data
  })
}

export function useLoginMutation() {
  return useAppMutation(async (credentials) => {
    const { data } = await api.post('/auth/login', credentials)
    return data
  })
}
